import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@tellescope/design-system";
import { SubmittedFormResponse } from "../FormResponseViewPage/SubmittedFormResponse";
import { PendingFormView } from "./PendingFormView";
import { useFormAccessCodeInfo } from "./useFormAccessCodeInfo";

/**
 * The legacy portal's `/documents/forms/:accessCode` route, where form group links land after
 * `/documents?groupId=` resolves the first open form. Submitted responses render read-only; pending
 * ones open for completion and continue to the next form in their group once submitted.
 */
export const FormAccessCodePage = () => {
  const { t } = useTranslation("forms");
  const { accessCode = "" } = useParams<{ accessCode: string }>();

  const { info, isLoading, error, wasSubmittedOnArrival } = useFormAccessCodeInfo(accessCode);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center text-muted-foreground">
          <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin" />
          <p className="text-sm">{t("Loading form...")}</p>
        </div>
      </div>
    );
  }

  // Only without data: a failed refetch after submitting here keeps the loaded response, and the
  // wizard's thanks screen should stay up rather than give way to an error.
  if (!info) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-sm font-medium text-destructive mb-1">{t("Unable to load form")}</p>
          <p className="text-xs text-muted-foreground mb-4">{error?.message ?? t("Form not found")}</p>
          <Button asChild variant="outline">
            <Link to="/documents">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t("Go Back")}
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  if (wasSubmittedOnArrival) {
    return <SubmittedFormResponse formResponse={info.response} />;
  }

  // Keyed so continuing to the next form in the group remounts with a fresh iframe token, which the
  // legacy renderer consumes on load.
  return <PendingFormView key={info.response.id} formResponse={info.response} form={info.form} fields={info.fields} />;
};
